/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Set default quantity to 2 in quick view modal for all products in Best Sellers collection
 * except for "SHAPERLUV™ TANK 2.0"
 */
document.addEventListener('DOMContentLoaded', function() {
  // Listen for drawer or popover content being loaded
  document.addEventListener('content:loaded', function(event) {
    const container = event.target;
    
    // Check if this is a quick-buy-drawer or quick-buy-popover
    if (container.tagName === 'QUICK-BUY-DRAWER' || container.tagName === 'QUICK-BUY-POPOVER') {
      // Check if this product is from Best Sellers collection using the data attribute
      if (container.hasAttribute('data-best-seller')) {
        // Find the product title to exclude SHAPERLUV™ TANK 2.0
        const productTitleElement = container.querySelector('.product-item-meta__title');
        const productTitle = productTitleElement ? productTitleElement.textContent.trim() : '';
        
        // Only set quantity to 2 if it's not the excluded product
        if (productTitle !== 'SHAPERLUV™ TANK 2.0') {
          // Find the quantity input within the container
          const quantityInput = container.querySelector('input[name="quantity"]');
          
          if (quantityInput) {
            // Set the default quantity to 2
            quantityInput.value = '2';
            quantityInput.size = '1';
            
            // If the input is using the custom input-number element, update its internal state
            if (quantityInput.hasAttribute('is') && quantityInput.getAttribute('is') === 'input-number') {
              // Trigger change event to ensure the component updates
              quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      }
    }
  });
  
  // Also handle cases where quick view might be opened without a content:loaded event
  const handleMutations = function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        const target = mutation.target;
        
        // Check if this is a quick-buy-drawer or quick-buy-popover that was just opened
        if ((target.tagName === 'QUICK-BUY-DRAWER' || target.tagName === 'QUICK-BUY-POPOVER') && 
            target.hasAttribute('open')) {
          
          // Use setTimeout to ensure the content has been loaded
          setTimeout(function() {
            // Check if this product is from Best Sellers collection using the data attribute
            if (target.hasAttribute('data-best-seller')) {
              // Find the product title to exclude SHAPERLUV™ TANK 2.0
              const productTitleElement = target.querySelector('.product-item-meta__title');
              const productTitle = productTitleElement ? productTitleElement.textContent.trim() : '';
              
              // Only set quantity to 2 if it's not the excluded product
              if (productTitle !== 'SHAPERLUV™ TANK 2.0') {
                const quantityInput = target.querySelector('input[name="quantity"]');
                
                if (quantityInput) {
                  quantityInput.value = '2';
                  quantityInput.size = '1';
                  
                  if (quantityInput.hasAttribute('is') && quantityInput.getAttribute('is') === 'input-number') {
                    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }
              }
            }
          }, 100);
        }
      }
    });
  };
  
  // Create a mutation observer to watch for quick view modals being opened
  const observer = new MutationObserver(handleMutations);
  
  // Start observing the document body for added nodes
  observer.observe(document.body, { 
    childList: true, 
    subtree: true,
    attributes: true,
    attributeFilter: ['open']
  });
});



(() => {
  'use strict';
  const testInfo = {
    className: 'slv-com-test',
    debug: 0,
    testName: 'SLV: Community Test',
    testVersion: 'v1'
  };

  const waitForElement = (selector) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }
      const observer = new MutationObserver((mutations) => {
        if (document.querySelector(selector)) {
          resolve(document.querySelector(selector));
          observer.disconnect();
        }
      });
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    });
  };

  const cartHTML = `
    <div class="slv-com-cart">
      <div class="mu-one-check-upsell mu-rounded mu-flex mu-items-center mu-space-x-1 mu-cursor-pointer mu-py-0" data-loading="false" style="border-color: rgb(226, 232, 240);">
        <div class="slv-com-cart-left">
          <div class="slv-com-toggle-wrapper">
            <input type="checkbox" id="slv-com-toggle" class="slv-com-toggle-checkbox">
            <label for="slv-com-toggle" class="slv-com-toggle-label">
              <div class="slv-com-toggle-bundle">
                <span class="slv-com-toggle-inner"></span>
                <span class="slv-com-toggle-switch"></span>
              </div>
              <div class="slv-com-left-text">
                <h4>Shaperluv - Fit Dad Program</h4>
                <p>Get 14 days free trial</p>
              </div>
            </label>
          </div>
        </div>
        <div class="slv-com-right">
          <p class="slv-com-prev-price">$99.99</p>
          <p class="slv-com-cur-price">Free</p>
        </div>
      </div>
    </div>
  `;

  const getPrice = () => {
    fetch('https://shaperluv.com/products/fit-dad-academy-community-free.json')
      .then(res => res.json())
      .then(data => {
        const firstVariant = data.product.variants[0];
        const price = firstVariant.price;
        const formattedPrice = _pf_formatMoney(price.replaceAll('.', ''), mu_money_format);
        document.querySelector('.slv-com-prev-price').innerHTML = formattedPrice;
      });
  }

  const addToCartRequest = (id) => {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: id,
        quantity: 1,
        selling_plan: 693113782619
      })
    })
      .then(response => {
        if (!response.ok) {
          flag = 0;
          throw new Error('Failed to add to cart');
        }
        return response.json();
      })
      .then(data => {
        if (document.querySelector('.mu_openned')) {
          monster_refresh();
        } else {
          waitForElement('.mu_openned').then(() => {
            monster_refresh();
            document.querySelector('.slv-com-cart').classList.remove('slv-com-busy');
            document.querySelector('#slv-com-toggle').checked = true;
          });
        }
        document.querySelector('.slv-com-cart').classList.remove('slv-com-busy');
        flag = 0;
      })
      .catch(error => {
        console.error('Error:', error);
      });
  }

  let flag = 0;

  let onlyOnce = 0;

  const initCart = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type == 'attributes' || mutation.type == 'childList') {
          if (document.querySelector('.mu-subtotal') && !document.querySelector('.slv-com-cart')) {
            document.querySelector('.mu-subtotal').insertAdjacentHTML('beforebegin', cartHTML);
            getPrice();
            if (!document.querySelector('.mu-cart-item[data-variant-id="50920246739291"]') && document.querySelector('#slv-com-toggle') && !document.querySelector('#monster-cart-wrapper > div > .mu-absolute [data-icon="spinner-third"]') && document.querySelectorAll('#mu-items-container .mu-cart-item').length > 0 && flag == 0) {
              flag = 1;
              setTimeout(() => {
                addToCartRequest(50920246739291);
              }, 500);
            }
          }

          if (document.querySelector('.mu-cart-item[data-variant-id="50920246739291"]') && !document.querySelector('#slv-com-toggle:checked') && flag == 0 && !document.querySelector('#monster-cart-wrapper > div > .mu-absolute [data-icon="spinner-third"]')) {
            document.querySelector('#slv-com-toggle').checked = true;
          }

          if (!document.querySelector('.mu-cart-item[data-variant-id="50920246739291"]') && document.querySelector('#slv-com-toggle') && !document.querySelector('#slv-com-toggle:checked') && flag == 1 && !document.querySelector('#monster-cart-wrapper > div > .mu-absolute [data-icon="spinner-third"]')) {
            flag = 0;
          }

          if (document.querySelector('.mu_openned') && document.querySelector('#slv-com-toggle:checked') && !document.querySelector('.mu-cart-item[data-variant-id="50920246739291"]') && flag == 0 && onlyOnce == 0 && !document.querySelector('#monster-cart-wrapper > div > .mu-absolute [data-icon="spinner-third"]')) {
            onlyOnce = 1;
            monster_refresh();
          }

          if (document.querySelector('.mu-cart-item[data-variant-id="50920246739291"] .mu-remove-bin') && document.querySelectorAll('#mu-items-container .mu-cart-item').length == 1 && document.querySelector('.mu-cart-item[data-variant-id="50920246739291"]') && !document.querySelector('#monster-cart-wrapper > div > .mu-absolute [data-icon="spinner-third"]')) {
            document.querySelector('.mu-cart-item[data-variant-id="50920246739291"] .mu-remove-bin').click();
          }
        }
      });
    });
    observer.observe(document.querySelector('#monster-cart-wrapper'), {
      childList: true,
      subtree: true,
      attributes: true
    });

    document.addEventListener('change', (e) => {
      if (e.target.closest('#slv-com-toggle')) {
        if (e.target.closest('#slv-com-toggle:checked') && !document.querySelector('.mu-cart-item[data-variant-id="50920246739291"]') && flag == 0) {
          flag = 1;
          setTimeout(() => {
            addToCartRequest(50920246739291);
          }, 500);
          document.querySelector('.slv-com-cart').classList.add('slv-com-busy');
        } else if (!e.target.closest('#slv-com-toggle:checked') && document.querySelector('.mu-cart-item[data-variant-id="50920246739291"]') && flag == 0) {
          flag = 1
          document.querySelector('.mu-cart-item[data-variant-id="50920246739291"] .mu-remove-bin').click();
        }
      }
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('.mu-cart-item[data-variant-id="50920246739291"] .mu-remove-bin') && document.querySelector('#slv-com-toggle:checked')) {
        document.querySelector('#slv-com-toggle').click();
      }
    });
  }

  const loadTest = () => {
    const bodyEle = document.body;
    if (bodyEle.classList.contains(testInfo.className)) {
      return;
    }

    bodyEle.classList.add(testInfo.className);

    document.querySelector('.product-form .product-form__image').innerHTML = '<img class="slv-com-img" src="https://shaperluv.com/cdn/shop/files/Untitled_design_-_2024-11-20T114331.868_38535e25-a9a4-4627-8582-84a654952dc6.png?v=1732099471&width=500" alt="Sale">';

    waitForElement('#monster-cart-wrapper #mu-header').then(() => {
      initCart();
    });
  };

  waitForElement('.product-form .product-form__image').then(() => {
    loadTest();
  });
})();

// Custom code for remove product in monster cart not working properly
(() => {
  const GIFT_VARIANT_ID = 50920246739291;       // ← your gift variant
  const GIFT_SELLING_PLAN = 693113782619;       // ← keep if needed
  const QUALIFYING_RULE = (item) => item.id !== GIFT_VARIANT_ID; // qualify = any non-gift left

  let syncing = false;
  const $toggle = () => document.getElementById('slv-com-toggle');

  // --- helpers ----------------------------------------------------
  const fetchCart = () => fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

  const hasGift = (cart) => cart.items.some(i => i.variant_id === GIFT_VARIANT_ID);
  const hasQualifying = (cart) => cart.items.some(i => QUALIFYING_RULE(i));

  const addGift = async () => {
    syncing = true;
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: GIFT_VARIANT_ID, quantity: 1, selling_plan: GIFT_SELLING_PLAN })
    });
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    syncing = false;
  };

  const removeGift = async () => {
    // Using updates is idempotent and does not need a line key
    syncing = true;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [GIFT_VARIANT_ID]: 0 } })
    });
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    syncing = false;
  };

  const setBusy = (on) => {
    const box = document.querySelector('.slv-com-cart');
    if (box) box.classList.toggle('slv-com-busy', !!on);
  };

  const syncToggleWithCart = (cart) => {
    const t = $toggle();
    if (!t) return;
    const shouldBeChecked = hasGift(cart);
    if (t.checked !== shouldBeChecked) t.checked = shouldBeChecked;
  };

  // Debounced reconcile to collapse rapid DOM mutations from the app
  let debTimer;
  const debounce = (fn, wait=250) => (...args) => {
    clearTimeout(debTimer);
    debTimer = setTimeout(() => fn(...args), wait);
  };

  const reconcile = debounce(async () => {
    if (syncing) return;
    const cart = await fetchCart();

    // keep UI toggle in sync with real cart
    syncToggleWithCart(cart);

    // RULES:
    // 1) If no qualifying items remain → ensure gift is removed.
    // 2) If toggle is ON and qualifying items exist → ensure gift is present.
    // 3) If toggle is OFF → ensure gift is removed.
    const t = $toggle();
    const tChecked = t ? t.checked : false;

    if (!hasQualifying(cart) && hasGift(cart)) {
      setBusy(true);
      await removeGift();
      setBusy(false);
      return;
    }

    if (tChecked && hasQualifying(cart) && !hasGift(cart)) {
      setBusy(true);
      await addGift();
      setBusy(false);
      return;
    }

    if (!tChecked && hasGift(cart)) {
      setBusy(true);
      await removeGift();
      setBusy(false);
    }
  }, 200);

  // --- event wiring ----------------------------------------------
  // 1) Theme-level event (if available)
  document.addEventListener('cart:updated', reconcile);

  // 2) Monster Cart DOM observer as a fallback
  const startObserver = () => {
    const host = document.querySelector('#monster-cart-wrapper');
    if (!host) return;

    new MutationObserver(() => reconcile()).observe(host, {
      childList: true, subtree: true, attributes: true
    });
  };

  // 3) Toggle control
  document.addEventListener('change', (e) => {
    const t = $toggle();
    if (!t || e.target !== t) return;
    // Just reconcile; it will add/remove via update.js as needed
    reconcile();
  });

  // 4) Also reconcile when the cart drawer opens
  document.addEventListener('content:loaded', reconcile);
  document.addEventListener('DOMContentLoaded', () => {
    startObserver();
    reconcile();
  });

  // Safety: if the cart becomes empty, nuke gift
  window.addEventListener('focus', reconcile);
})();

// Custom code for remove product in monster cart not working properly